
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { PrizeAllocation, Prize, University } from "./types";

export function usePrizeAllocationsData() {
  const { toast } = useToast();
  const [allocations, setAllocations] = useState<PrizeAllocation[]>([]);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      console.log('Fetching prize allocations data...');
      
      const [allocationsResult, prizesResult, universitiesResult] = await Promise.all([
        supabase
          .from('prize_allocations')
          .select(`
            id,
            university_id,
            prize_id,
            daily_limit,
            allocated_count,
            created_at,
            last_reset_at,
            universities:university_id (id, name),
            prizes:prize_id (id, name)
          `),
        
        supabase
          .from('prizes')
          .select('id, name')
          .order('name'),
        
        supabase
          .from('universities')
          .select('id, name')
          .order('name')
      ]);

      if (allocationsResult.error) throw allocationsResult.error;
      if (prizesResult.error) throw prizesResult.error;
      if (universitiesResult.error) throw universitiesResult.error;

      console.log('Raw allocations data:', allocationsResult.data);
      
      // Transform the data to match the expected format
      const transformedAllocations = allocationsResult.data.map(allocation => ({
        ...allocation,
        universities: allocation.universities || { id: allocation.university_id, name: 'Unknown' },
        prizes: allocation.prizes || { id: allocation.prize_id, name: 'Unknown' }
      }));

      console.log('Transformed allocations:', transformedAllocations);

      setAllocations(transformedAllocations);
      setPrizes(prizesResult.data || []);
      setUniversities(universitiesResult.data || []);

    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    allocations,
    prizes,
    universities,
    isLoading,
    refetch: fetchData
  };
}
